import { DEFAULT_SECTION_2, DEFAULT_SECTION_4 } from "@/constants/sections";

import { AppCard } from "@/components/ui/AppCard";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageLayout } from "../layout";
import { useStore } from "@/store";
import { getDashboardCacheKey } from "@/store/dashboard";
import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_DEAL_NETWORK_RECOMMENDATION_FILTER,
  type TDealNetworkRecommendationFilter,
} from "./features/dealNetworkFilters";
import { getSectionRenderer } from "./sectionRenderers";

function SectionDashboardSkeleton() {
  return (
    <PageLayout>
      {/* Header skeleton — matches the flat PageHeader */}
      <div className="flex-shrink-0 px-0.5">
        <Skeleton className="mb-3 h-6 w-48" />
        <Skeleton className="h-4 w-full max-w-2xl" />
        <Skeleton className="mt-1.5 h-4 w-3/4 max-w-xl" />
      </div>

      {/* Content skeleton */}
      <div className="min-h-0 flex-1 space-y-4 overflow-auto">
        {/* Filter pill bar skeleton */}
        <div className="rounded-xl border border-border bg-card p-3 shadow-sm sm:rounded-2xl sm:p-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <Skeleton className="h-8 w-10 flex-shrink-0 rounded-full" />
            <Skeleton className="h-8 w-20 flex-shrink-0 rounded-full" />
            <Skeleton className="h-8 w-24 flex-shrink-0 rounded-full" />
            <Skeleton className="h-8 w-16 flex-shrink-0 rounded-full" />
            <Skeleton className="h-8 w-20 flex-shrink-0 rounded-full" />
          </div>
        </div>

        {/* Two-column content cards */}
        <div className="flex flex-col gap-4 md:flex-row">
          <AppCard className="min-w-0 flex-1 space-y-3 p-4">
            <Skeleton className="h-5 w-36" />
            <div className="space-y-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-[92%]" />
              <Skeleton className="h-3 w-[80%]" />
              <Skeleton className="h-3 w-[68%]" />
            </div>
            <div className="space-y-2 pt-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-[85%]" />
              <Skeleton className="h-3 w-[60%]" />
            </div>
          </AppCard>
          <AppCard className="min-w-0 flex-1 space-y-3 p-4 md:flex-[0_0_35%]">
            <Skeleton className="h-5 w-28" />
            <div className="flex justify-center py-4">
              <Skeleton className="h-28 w-28 rounded-full" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="mt-2 h-8 w-full rounded-lg" />
          </AppCard>
        </div>
      </div>
    </PageLayout>
  );
}

export type TDashboardFeatureConfig = {
  showCompanyKnowledge: boolean;
  showCategoriesTools: boolean;
  showChat: boolean;
  showInvestorReadinessTodos?: boolean;
};

type TBaseSectionDashboardProps = {
  sectionId: string | null;
  sectionName: string;
  description?: string | null;
  companyId?: string | null;
  enabledFeatures: TDashboardFeatureConfig;
};

export function BaseSectionDashboard({
  sectionId,
  sectionName,
  description,
  companyId,
  enabledFeatures,
}: TBaseSectionDashboardProps) {
  const getSectionForCompany = useStore((state) => state.getSectionForCompany);
  const result = companyId && sectionId ? getSectionForCompany(companyId, sectionId) : null;
  const company = result?.company ?? null;
  const fetchSectionDashboard = useStore((state) => state.fetchSectionDashboard);
  const getSectionDashboard = useStore((state) => state.getSectionDashboard);
  const loadingSections = useStore((state) => state.loadingSections);
  const errors = useStore((state) => state.errors);

  useEffect(() => {
    if (!sectionId || !companyId) return;
    void fetchSectionDashboard(sectionId, { force: false, companyId });
  }, [sectionId, companyId, fetchSectionDashboard]);

  const dashboardCacheKey = sectionId ? getDashboardCacheKey(sectionId, companyId) : null;
  const dashboardData = sectionId ? getSectionDashboard(sectionId, companyId) : null;
  const isLoading = dashboardCacheKey ? loadingSections.has(dashboardCacheKey) : false;
  const error = dashboardCacheKey ? (errors[dashboardCacheKey] ?? null) : null;

  const [dealNetworkFilter, setDealNetworkFilter] = useState<TDealNetworkRecommendationFilter>(
    DEFAULT_DEAL_NETWORK_RECOMMENDATION_FILTER
  );
  const [readinessCategoryFilter, setReadinessCategoryFilter] = useState<{
    selectedCategoryIds: string[];
    isAllSelected: boolean;
  }>({ selectedCategoryIds: [], isAllSelected: true });
  const [readinessTodoRefreshCounter, setReadinessTodoRefreshCounter] = useState(0);
  const handleReadinessTodoRefresh = useCallback(() => {
    setReadinessTodoRefreshCounter((prev) => prev + 1);
  }, []);

  const handleReadinessCategorySelection = useCallback(
    (selection: { selectedCategoryIds: string[]; isAllSelected: boolean }) => {
      setReadinessCategoryFilter({
        selectedCategoryIds: selection.selectedCategoryIds,
        isAllSelected: selection.isAllSelected,
      });
    },
    []
  );

  useEffect(() => {
    if (sectionId !== DEFAULT_SECTION_4) {
      setDealNetworkFilter(DEFAULT_DEAL_NETWORK_RECOMMENDATION_FILTER);
    }
  }, [sectionId]);

  useEffect(() => {
    if (sectionId !== DEFAULT_SECTION_2) {
      setReadinessCategoryFilter({ selectedCategoryIds: [], isAllSelected: true });
    }
  }, [sectionId]);

  const handleDealNetworkCategorySelection = useCallback(
    (selection: { selectedCategoryIds: string[]; isAllSelected: boolean }) => {
      setDealNetworkFilter((prev) => {
        const isExtraFilterActive = prev.type === "shortlisted" || prev.type === "dismissed";
        if (isExtraFilterActive && selection.isAllSelected && selection.selectedCategoryIds.length === 0) {
          return prev;
        }
        if (selection.isAllSelected || selection.selectedCategoryIds.length === 0) {
          return DEFAULT_DEAL_NETWORK_RECOMMENDATION_FILTER;
        }
        return {
          type: "categories",
          categoryIds: selection.selectedCategoryIds,
        };
      });
    },
    []
  );

  const handleDealNetworkFilterButtonChange = useCallback((filterId: string | null) => {
    if (!filterId) {
      setDealNetworkFilter(DEFAULT_DEAL_NETWORK_RECOMMENDATION_FILTER);
      return;
    }
    if (filterId === "shortlisted") {
      setDealNetworkFilter({ type: "shortlisted" });
    } else if (filterId === "dismissed") {
      setDealNetworkFilter({ type: "dismissed" });
    } else {
      setDealNetworkFilter(DEFAULT_DEAL_NETWORK_RECOMMENDATION_FILTER);
    }
  }, []);

  const dealNetworkFilterButtonId =
    dealNetworkFilter.type === "shortlisted"
      ? "shortlisted"
      : dealNetworkFilter.type === "dismissed"
        ? "dismissed"
        : null;

  const categoriesCommonProps = {
    dashboardData,
    isLoading,
    error,
    companyId: companyId ?? null,
  };

  const hasCompanyKnowledge = enabledFeatures.showCompanyKnowledge;
  const hasCategoriesTools = enabledFeatures.showCategoriesTools;
  const hasChat = enabledFeatures.showChat;

  const context = {
    sectionId,
    sectionName,
    description,
    companyId,
    companyName: company?.name ?? null,
    dashboardData,
    isLoading,
    error,
    enabledFeatures,
    hasCompanyKnowledge,
    hasCategoriesTools,
    hasChat,
    categoriesCommonProps,
    dealNetworkFilter,
    dealNetworkFilterButtonId,
    handleDealNetworkCategorySelection,
    handleDealNetworkFilterButtonChange,
    readinessCategoryFilter,
    handleReadinessCategorySelection,
    readinessTodoRefreshCounter,
    handleReadinessTodoRefresh,
  };

  if (isLoading) {
    return <SectionDashboardSkeleton />;
  }

  const renderer = getSectionRenderer(sectionId);
  return <>{renderer(context)}</>;
}
